import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Film, Mic, Captions } from "lucide-react";
import { adminApi } from "@/lib/api";

const VIDEO_STATUS = {
  WAITING: "#A7ABB0",
  SCRIPTING: "#f5c451",
  "READY TO RENDER": "#4ea3ff",
  RENDERING: "#9b8cff",
  QC: "#FF8A1F",
  APPROVED: "#3ecf8e",
  PUBLISHED: "#F5F5F2",
  FAILED: "#f87171",
};

export default function VideoQueue() {
  const { data: videos = [], isLoading } = useQuery({ queryKey: ["videos"], queryFn: adminApi.videos });

  return (
    <div className="max-w-[1100px]">
      <header className="mb-6">
        <div className="td-eyebrow mb-2" style={{ color: "#FF8A1F" }}>
          Short-form Video
        </div>
        <h1 className="font-display font-extrabold text-white text-3xl tracking-tight">Video Queue</h1>
        <p className="text-[#71767c] mt-1 font-mono-td text-xs">9:16 vertical specs for TikTok, Reels & Shorts. Rendering pipeline not connected.</p>
      </header>

      {isLoading ? (
        <p className="text-[#A7ABB0]">Loading…</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {videos.map((v) => {
            const color = VIDEO_STATUS[v.status] || "#A7ABB0";
            return (
              <div key={v.id} className="rounded-lg border p-5" style={{ borderColor: "rgba(255,255,255,0.08)", background: "#111315" }} data-testid={`video-card-${v.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <h3 className="font-display font-semibold text-white text-sm leading-snug">{v.story}</h3>
                  <span
                    className="td-eyebrow shrink-0 px-2 py-1 rounded"
                    style={{ color, background: "rgba(255,255,255,0.05)", fontSize: "0.5rem" }}
                  >
                    {v.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 td-eyebrow mb-4" style={{ color: "#71767c", fontSize: "0.55rem" }}>
                  <span>{v.platform}</span>
                  <span className="opacity-40">/</span>
                  <span>{v.duration}</span>
                  <span className="opacity-40">/</span>
                  <span>9:16</span>
                </div>
                <div className="flex items-center gap-4 pt-3 border-t" style={{ borderColor: "rgba(255,255,255,0.07)" }}>
                  <Asset icon={Film} label="Script" on={v.has_script} />
                  <Asset icon={Mic} label="Voiceover" on={v.has_voiceover} />
                  <Asset icon={Captions} label="Subtitles" on={v.has_subtitles} />
                  <span className="ml-auto font-mono-td" style={{ color: "#71767c", fontSize: "0.6rem" }}>
                    Render: {v.render_status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Asset({ icon: Icon, label, on }) {
  return (
    <div className="flex items-center gap-1.5" title={label}>
      <Icon size={14} style={{ color: on ? "#3ecf8e" : "#4a4e52" }} />
      <span className="td-eyebrow" style={{ color: on ? "#A7ABB0" : "#4a4e52", fontSize: "0.5rem" }}>
        {label}
      </span>
    </div>
  );
}
